/* eslint-disable react/jsx-no-bind */

// prop-type is a library for typechecking of props
import PropTypes from "prop-types";
import { useFormikContext } from "formik";

// @mui material components
import Grid from "@mui/material/Grid2";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// NewPartner page components
import FormField from "components/FormField";
import DetailList from "components/DetailList";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { getOneUser } from "store/actions/userActions";
import TravelStylePicker from "components/AutoComplete/TravelStylePicker";
import Danger from "components/Danger";
import ImagePicker from "components/AutoComplete/ImagePicker";
import UploadImage from "pages/content/locations/new-location/components/Sliders/UploadImage";
import CuratorWithDestination from "./CuratorWithDestination";

const ItineraryInfo = ({
  formField,
  detailPage = true,
  editPage = false,
  handleCuratorChange = () => {},
  selectedCurator = {},
  displayErr = false,
}) => {
  const { values, errors, touched, setFieldValue } = useFormikContext();

  const { title, introText, travelStyle, curator, days, destination, images } = formField;

  const { title: titleV, introText: introTextV, travelStyle: travelStyleV, days: daysV } = values;

  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source");
  const sourceId = searchParams.get("sourceId");

  const isProduction = process.env.NODE_ENV === "production";
  const apiBaseUrl = isProduction ? "" : "https://staging.";

  const [travelStyleSelected, setTravelStyleSelected] = useState({});

  const curatorUser = useSelector((state) => state.users.specificUser);

  useEffect(() => {
    if (source === "users" && sourceId) {
      dispatch(getOneUser(sourceId));
    }
  }, [source, sourceId]);

  useEffect(() => {
    if (travelStyleV) {
      setTravelStyleSelected({ label: travelStyleV, value: travelStyleV });
    }
  }, [travelStyleV]);

  useEffect(() => {
    if (curatorUser) {
      handleCuratorChange({
        label: curatorUser?.name,
        value: curatorUser?._id,
        destinations: curatorUser?.destinations,
      });
      setFieldValue("curator", {
        label: curatorUser?.name,
        value: curatorUser?._id,
        destinations: curatorUser?.destinations,
      });
    }
  }, [curatorUser]);

  return (
    <MDBox>
      {!editPage ? (
        <MDBox lineHeight={0}>
          <MDTypography variant="h5">Info</MDTypography>
        </MDBox>
      ) : (
        ""
      )}
      {detailPage && editPage ? (
        <DetailList
          data={{
            title: values?.title,
            curator: values?.curator?.name,
            destination: values?.destination?.destinationName,
            days: `${values?.days} ${values?.days > 1 ? "days" : "day"}`,
            // city: values?.city,
            travelStyle: values?.travelStyle,
            companion: values?.companion,
            interests: values?.interests,
            introText: values?.introText,
            coverPhoto: values?.images,
            url: `${apiBaseUrl}lokalee.app/curator/trip/${values?.slug}`,
          }}
        />
      ) : (
        <MDBox mt={1.625}>
          <Grid container spacing={3}>
            {!editPage ? (
              <Grid size={{ xs: 12 }}>
                <ImagePicker edit imageType="logo" />
              </Grid>
            ) : (
              ""
            )}
            <CuratorWithDestination
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
              handleCuratorChange={handleCuratorChange}
              curator={curator}
              source={source}
              selectedCurator={selectedCurator}
              sourceId={sourceId}
              displayErr={displayErr}
              destination={destination}
            />
            <Grid size={{ xs: 12 }}>
              <FormField
                type={title.type}
                label={title.label}
                name={title.name}
                value={titleV}
                placeholder={title.placeholder}
                error={errors.title && touched.title}
                success={titleV?.length > 0 && !errors.title}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <MDTypography variant="regular" style={{ marginRight: "0.5rem", fontSize: "14px" }}>
                {travelStyle?.label}
              </MDTypography>
              <TravelStylePicker
                travelStyleV={travelStyleSelected || {}}
                setTravelStyle={setTravelStyleSelected}
                setFieldValue={setFieldValue}
              />
              <Danger validation={errors?.travelStyle} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormField
                label={introText.label}
                name={introText.name}
                value={introTextV}
                multiline
                outlined
                rows={5}
                placeholder={introText.placeholder}
                error={errors.introText && touched.introText}
                success={introTextV?.length > 0 && !errors.introText}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormField
                type={days.type}
                label={days.label}
                name={days.name}
                value={daysV}
                placeholder={days.placeholder}
                error={errors?.days && touched?.days}
                success={daysV?.length > 0 && !errors?.days}
                disabled={editPage}
              />
            </Grid>
            <Grid size={{ sm: 12 }}>
              <MDTypography variant="button" fontWeight="regular" color="text">
                {images.label}
              </MDTypography>
              <UploadImage
                key={values?.images[0]}
                defaultImage={values?.images?.[0]}
                setFieldValue={setFieldValue}
                fieldName="images"
              />

              {errors?.images && touched?.images && <Danger validation={errors?.images} />}
            </Grid>
          </Grid>
        </MDBox>
      )}
    </MDBox>
  );
};

// typechecking props for ItineraryInfo
ItineraryInfo.propTypes = {
  formField: PropTypes.instanceOf(Object).isRequired,
  detailPage: PropTypes.bool,
  editPage: PropTypes.bool,
  handleCuratorChange: PropTypes.func,
  selectedCurator: PropTypes.instanceOf(Object),
  displayErr: PropTypes.bool,
};

export default ItineraryInfo;
